import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  PopoverContentProps,
  PopoverProps as RadixPopoverProps,
  PopoverTriggerProps as RadixPopoverTriggerProps,
  PopoverArrowProps,
} from '@radix-ui/react-popover';
import { createGlobalStyle, styled, css } from 'styled-components';
import { SpacingProps } from '../box';
import { withSpacing } from '../../utils';
import colors from '../../style/colors';
import PopoverContentContainer from './popover-content-container';
import { sizes } from './style';
import AnimatedPopoverContent from './animated-popover-content';

type PopoverChildrenAsFunctionType = ({
  isOpen,
  setIsOpen,
}: {
  isOpen?: boolean;
  hide: () => void;
  setIsOpen: (open: boolean) => void;
}) => React.JSX.Element;

interface PopoverProps extends Omit<RadixPopoverProps, 'children'> {
  children: PopoverChildrenAsFunctionType | RadixPopoverProps['children'];
}

const GlobalStyle = createGlobalStyle`
    [data-radix-popper-content-wrapper] {
        z-index: 9999 !important;
    }
`;

interface ArrowProps extends PopoverArrowProps, SpacingProps {}

const StyledArrow = styled(PopoverPrimitive.Arrow)<ArrowProps>`
  visibility: visible;
  fill: ${colors.white};
  ${withSpacing}
`;

const StyledPopoverContent = styled(AnimatedPopoverContent)<{ $withOverlay?: boolean }>`
  ${props =>
    props.$withOverlay &&
    css`
      box-shadow: 0 0 0 12000px rgba(0, 0, 0, 0.32);
      border-radius: 4px;
    `}
`;

const Popover: React.FC<PopoverProps> & {
  Trigger: React.FC<PopoverTriggerProps>;
  Content: React.FC<ContentProps>;
  HeadlessContent: React.FC<PopoverContentProps>;
  Arrow: React.FC<ArrowProps>;
} = ({ children, open, defaultOpen, ...rest }: PopoverProps) => {
  // this state is relevant only when children are passed as function - meaning that we want the popover to be controlled
  const [isOpen, setIsOpen] = React.useState(!!open);

  // developers can pass children as a render prop function for more control on the "controlled" popover
  if (typeof children === 'function') {
    return (
      <PopoverPrimitive.Root modal={true} open={isOpen} onOpenChange={open => setIsOpen(open)} {...rest}>
        <React.Fragment>
          {children({
            isOpen,
            setIsOpen,
            hide: () => setIsOpen(false),
          })}
          <GlobalStyle />
        </React.Fragment>
      </PopoverPrimitive.Root>
    );
  }

  // uncontrolled popover
  return (
    <React.Fragment>
      <PopoverPrimitive.Root defaultOpen={defaultOpen} open={open} {...rest}>
        {children}
      </PopoverPrimitive.Root>
      <GlobalStyle />
    </React.Fragment>
  );
};

export interface PopoverTriggerProps extends RadixPopoverTriggerProps {
  disabled?: boolean;
  children: React.ReactElement<any, any> | null;
}

Popover.Trigger = ({ asChild = true, children, disabled }: PopoverTriggerProps) => {
  if (disabled) {
    return children;
  }
  return <PopoverPrimitive.Trigger asChild={asChild}>{children}</PopoverPrimitive.Trigger>;
};

export interface ContentProps extends PopoverContentProps {
  size?: keyof typeof sizes;
  stopPropagation?: boolean;
  overflowY?: 'auto' | 'hidden' | 'scroll' | 'visible';
  height?: string | number;
  withOverlay?: boolean;
}

Popover.Content = ({
  children,
  side,
  align,
  size,
  sideOffset = 4,
  stopPropagation,
  overflowY,
  height,
  withOverlay,
}: ContentProps) => {
  return (
    <PopoverPrimitive.Portal>
      <StyledPopoverContent
        onClick={e => {
          if (stopPropagation) {
            e.stopPropagation();
          }
        }}
        sideOffset={sideOffset}
        side={side}
        align={align}
        $withOverlay={withOverlay}
      >
        <PopoverContentContainer overflowY={overflowY} size={size} height={height}>
          {children}
        </PopoverContentContainer>
      </StyledPopoverContent>
    </PopoverPrimitive.Portal>
  );
};

// should be use in rare cases when we need an un-styled popover content
Popover.HeadlessContent = ({ children, side, align, sideOffset = 4 }: PopoverContentProps) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.PopoverContent sideOffset={sideOffset} side={side} align={align}>
        {children}
      </PopoverPrimitive.PopoverContent>
    </PopoverPrimitive.Portal>
  );
};

Popover.Arrow = (props: ArrowProps) => {
  return <StyledArrow {...props} />;
};

export { PopoverProps, ArrowProps };
export default Popover;
